import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { cycleHdBodygraphGateInConfig } from '../bodygraph/hd-bodygraph-config.utils';
import {
  HdBodygraphInteractionMode,
  HdGatePrimaryAction,
} from '../bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import {
  HdBodygraphColorConfig,
  isHdBodygraphDarkCenterColor,
  resolveHdBodygraphCenterTextColor,
} from '../bodygraph/hd-bodygraph-color-config';
import { HD_BODYGRAPH_CHANNELS, HD_BODYGRAPH_GATE_TO_CENTER } from '../bodygraph/hd-bodygraph-metadata';
import { getHdGateIchingChar, getHdGateMetadata } from '../bodygraph/hd-gate-metadata';
import {
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
} from '../bodygraph/hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../bodygraph/hd-chart-hover-highlight.service';
import { getHdPlanetDisplayConfig } from '../bodygraph/planet-grid/hd-planet-display-config';
import {
  DEFAULT_MANDALA_GEOMETRY,
  HdMandalaQuarterColorVariant,
  HdMandalaZodiacRingVariant,
  MANDALA_GATE_SLICE_INNER_RADIUS_FACTOR,
  MOON_TRANSIT_TODAY_ICON_EDGE_OFFSET,
  MOON_TRANSIT_WHEEL_EDGE_RADIUS,
  MandalaGeometryConfig,
  MandalaInfoRingKey,
  MandalaRingRadii,
} from './mandala.constants';
import { describeAnnularSector, describeSpoke, normalizeAngle, polarToCartesian } from './mandala-geometry.utils';
import { HD_SEASON_MARKERS, HD_SEASON_MARKER_EDGE_OFFSET, HD_SEASON_MARKER_RADIUS } from './hd-season-markers';
import {
  buildMandalaGateSegments,
  buildMandalaGodheadSegments,
  buildMandalaIchingSymbols,
  buildMandalaLineSegments,
  buildMandalaQuarterSegments,
  buildMandalaZodiacSegments,
  gateSequenceIndex,
  longitudeToScreenAngle,
  MandalaGateSegment,
} from './mandala-layout';
import {
  HdMandalaMoonTransitViewData,
  HdMoonTransitFormationClickEvent,
} from './moon-transit/hd-moon-transit-view.models';
import { HdMoonTransitFormation } from './moon-transit/hd-moon-transit';
import { HD_JUST_NOW_TRANSIT_COLOR, HdJustNowTransitFormation } from './just-now-transit/hd-just-now-transit';
import { blendOver } from '@shared/utils/color.utils';
import {
  HdJustNowTransitFormationClickEvent,
  HdMandalaJustNowTransitViewData,
} from './just-now-transit/hd-just-now-transit-view.models';
import { HdMandalaLocksKeysViewData } from './locks-keys/hd-locks-keys-view.models';
import { HdMandalaPatternsViewData } from './patterns/hd-mandala-patterns-view.models';
import { HdActivePaletteService } from '../bodygraph/hd-active-palette.service';

/** Render-primitief van de patronen-overlay: één verbindingslijn (kanaal/partner/kruis). */
interface MandalaPatternConnectorView {
  readonly id: string;
  readonly pathD: string;
  /** De twee poorten van de lijn — voor hover-highlight langs beide kanten. */
  readonly gateA: number;
  readonly gateB: number;
}

/** Ring-markering op een incarnatiekruis-poort in de patronen-overlay. */
interface MandalaPatternMarkerView {
  readonly id: string;
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

/**
 * Debounce-vertraging (ms) voor de lokale hover-signalen van het wiel. Gelijk
 * gekozen aan de enter/leave-delay van {@link HdChartHoverHighlightService}
 * (72ms) zodat de mandala dezelfde "geen-flikkering bij snel slepen"-respons
 * vertoont als de bodygraph-chart en de planeetgrid.
 */
const HD_MANDALA_HOVER_DELAY_MS = 72;

/** Astronomicon-glyph van de maan — today-marker in de maan-transit modus. */
const HD_MANDALA_MOON_GLYPH = getHdPlanetDisplayConfig('moon').astronomiconChar;
/** Zonsymbool in het badge-je: buitencirkel en kernpunt (viewBox-eenheden). */
const MANDALA_SEASON_SUN_RING_RADIUS = 4;
const MANDALA_SEASON_SUN_DOT_RADIUS = 1.3;

/**
 * Font Awesome 7 Pro solid-glyphs (webfont, weight 900) voor de Locks & Keys-
 * laag: het slotje bij de lock-poorten en het sleuteltje bij de key-poorten,
 * beide net buiten de zichtbare wielrand (zelfde plaatsing als de today-marker).
 */
const HD_MANDALA_LOCK_GLYPH = '';
const HD_MANDALA_KEY_GLYPH = '';

/** Radiale afstand icoon-middelpunt tot de zichtbare wielrand (viewBox-units). */
const HD_MANDALA_LOCKS_KEYS_ICON_EDGE_OFFSET = 14;

/**
 * Radiale afstand van de gebogen lock-keynote (rol, bv. "De Weg") tot de
 * zichtbare wielrand: ruim buiten de slot-iconen (ICON_EDGE_OFFSET) zodat de
 * naam vrij boven het slotje hangt in de brede marge die de Locks & Keys-modus
 * buiten het wiel vrijhoudt.
 */
const HD_MANDALA_LOCKS_KEYS_KEYNOTE_EDGE_OFFSET = 36;

/**
 * Halve boogbreedte (graden) van een lock-keynote, symmetrisch rond het
 * lock-midden. De acht locks staan 45° uit elkaar (de 8-puntige ster), dus een
 * boog van 2×20° laat ruim marge tussen buurlabels; de tekst zelf staat via
 * `startOffset: 50%` gecentreerd en vult maar het middendeel.
 */
const HD_MANDALA_LOCKS_KEYS_KEYNOTE_HALF_ANGLE = 20;

/**
 * Fontgrootte (viewBox-units) van de gebogen lock-keynotes; zelfde
 * baseline-compensatie-contract met de SCSS (`.mandala-locks-keys-keynote`) als
 * de kwartaal-keynotes.
 */
const HD_MANDALA_LOCKS_KEYS_KEYNOTE_FONT_SIZE = 13;

/** Lengte van de naar binnen wijzende punt aan een lock-venster (vóór wiel-schaal). */
const HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH = 15;

/** Halve hoekbreedte (graden) van de pointer-basis; de poortcel zelf is 5,625°. */
const HD_MANDALA_LOCKS_KEYS_POINTER_HALF_ANGLE = 1.55;

/**
 * Fontgrootte (viewBox-units) van de gebogen kwartaal-keynotes. Moet gelijk
 * blijven aan de SCSS `font-size` van `.mandala-quarter__label`: de boog-straal
 * wordt hiermee gecompenseerd zodat de tekst optisch in het midden van de
 * kwartaalband ligt (de textPath-baseline ligt óp de boog).
 */
const HD_MANDALA_QUARTER_KEYNOTE_FONT_SIZE = 15;

/**
 * Fontgrootte (viewBox-units) van de gebogen godhead-namen; zelfde
 * baseline-compensatie-contract met de SCSS als de kwartaal-keynotes. Kleiner
 * dan de keynotes: 16 segmenten van 22,5° bieden per naam een kortere boog
 * ("Keepers of the Wheel" is de maatgevende langste). 11 → 12 + bold
 * (finetune 2026-08-20).
 */
const HD_MANDALA_GODHEAD_FONT_SIZE = 12;

/**
 * Maatvoering van de planeetactivatie-glyphs in de activatie-zone
 * (viewBox-units). `FONT` moet gelijk blijven aan de SCSS `font-size` van
 * `.mandala-activation-glyph`; `STEP` is de radiale afstand tussen gestapelde
 * glyphs van dezelfde poort; `EDGE_PADDING` houdt de buitenste glyph los van
 * de wielrand. Past een stapel niet tot aan de zone-rand, dan wordt de step
 * samengedrukt zodat álle glyphs binnen de zone blijven.
 */
const HD_MANDALA_GLYPH_FONT_SIZE = 16;
const HD_MANDALA_GLYPH_STEP = 17;
const HD_MANDALA_GLYPH_EDGE_PADDING = 4;

/**
 * Eén planeetactivatie-glyph in de activatie-zone: de page lost de kleur al op
 * naar de bron-semantiek van de actieve weergave (design-rood / personality-
 * zwart / natal- of chart1-blauw / cycle-, partner- of transit-groen), zodat
 * het wiel bronneutraal blijft. De volgorde van de array bepaalt de
 * stapelvolgorde per poort (buiten → binnen).
 */
export interface HdMandalaGateGlyph {
  readonly key: string;
  readonly gate: number;
  readonly glyph: string;
  readonly color: string;
}

/**
 * Gelokaliseerde kwartaal-keynote-delen (titel + purpose per kwartaal-key).
 * De page levert deze in de actieve contenttaal aan; zonder input vallen de
 * keynotes terug op de Engelse canon uit de constants. Sternamen zijn
 * taal-neutraal en komen altijd uit de constants.
 */
export type HdMandalaQuarterTexts = Readonly<Record<string, { readonly title: string; readonly purpose: string }>>;

/** Gelokaliseerde dierenriem-namen per teken-key (tooltip op de band). */
export type HdMandalaZodiacNames = Readonly<Record<string, string>>;

/**
 * Gelokaliseerde tooltips van de seizoensmarkeringen (per `HdSeasonKey`) en van
 * de kwartaalbanden (per kwartaal-key); zie `buildHdMandalaSeasonTexts`.
 */
export type HdMandalaSeasonTexts = Readonly<{
  readonly seasons: Readonly<Record<string, string>>;
  readonly quarters: Readonly<Record<string, string>>;
}>;

interface MandalaGateView {
  readonly id: string;
  readonly gate: number;
  readonly pathD: string;
  readonly fill: string;
  readonly isActive: boolean;
  readonly labelX: number;
  readonly labelY: number;
  /** Lichte cijferkleur op een donkere centervulling; anders `undefined` (SCSS-kleur). */
  readonly labelFill: string | undefined;
}

interface MandalaLineView {
  readonly id: string;
  readonly gate: number;
  readonly line: number;
  readonly pathD: string;
  readonly isActive: boolean;
}

interface MandalaZodiacView {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly glyph: string;
  readonly pathD: string;
  readonly glyphX: number;
  readonly glyphY: number;
}

interface MandalaQuarterView {
  /** Native tooltip (SVG <title>) met start, midden en seizoen van het kwartaal, of null. */
  readonly tooltip: string | null;
  readonly id: string;
  readonly key: string;
  readonly label: string;
  /**
   * Keynote in drie delen — "Quarter of … · " / "Purpose through …" /
   * " · ster" — zodat het purpose-deel (het praktischst voor de gebruiker)
   * centraal staat en een tikje vetter gezet kan worden.
   */
  readonly keynoteLead: string;
  readonly keynotePurpose: string;
  readonly keynoteTail: string;
  readonly pathD: string;
  /** Id + pad van de onzichtbare boog waarlangs de keynote-tekst buigt. */
  readonly arcId: string;
  readonly arcD: string;
}

interface MandalaSliceView {
  readonly id: string;
  readonly pathD: string;
}

interface MandalaSliceCenterFillView {
  readonly id: string;
  readonly gate: number;
  readonly pathD: string;
  readonly fill: string;
  readonly opacity: number;
}

/** Dekking van de gefadede centerkleur in de slices. */
const MANDALA_SLICE_CENTER_FILL_OPACITY = 0.18;

interface MandalaGodheadView {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly quarterKey: string;
  readonly pathD: string;
  /** Id + pad van de onzichtbare boog waarlangs de naam buigt. */
  readonly arcId: string;
  readonly arcD: string;
}

interface MandalaGlyphView {
  readonly key: string;
  readonly gate: number;
  readonly glyph: string;
  readonly color: string;
  readonly x: number;
  readonly y: number;
}

interface MandalaIchingView {
  readonly id: string;
  readonly gate: number;
  readonly glyph: string;
  readonly glyphX: number;
  readonly glyphY: number;
}

/**
 * Markering van een maanpoort: een cirkel om het poortnummer óf een
 * venster-kader om het volledige poortsegment (gateMarkerStyle-setting).
 */
type MandalaMoonGateMarkerView =
  | { readonly id: string; readonly kind: 'ring'; readonly cx: number; readonly cy: number; readonly r: number }
  | { readonly id: string; readonly kind: 'frame'; readonly pathD: string };

interface MandalaMoonConnectorView {
  readonly id: string;
  readonly channelKey: string;
  readonly formation: HdMoonTransitFormation;
  readonly pathD: string;
  /** Einddot bij de partnerpoort (de poort die al in de chart zit). */
  readonly endX: number;
  readonly endY: number;
}

interface MandalaSeasonMarkerView {
  readonly id: string;
  readonly key: string;
  /** Middelpunt en straal van het ronde badge-je op de band. */
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
  readonly tooltip: string | null;
}

interface MandalaMoonTodayView {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly iconX: number;
  readonly iconY: number;
  readonly glyph: string;
  /** Native tooltip (SVG <title>) met de actuele maanpositie, of null. */
  readonly tooltip: string | null;
}

interface MandalaJustNowConnectorView {
  readonly id: string;
  readonly channelKey: string;
  readonly formation: HdJustNowTransitFormation;
  readonly pathD: string;
  readonly endX: number;
  readonly endY: number;
}

interface MandalaJustNowGateMarkerView {
  readonly id: string;
  readonly gate: number;
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

/**
 * Lock-venster in de Locks & Keys-laag: het venster-kader om de G-poort
 * (hergebruik van het exacte poortsegment-pad, zoals de maan-frame), de naar
 * binnen wijzende punt in de spie en het slot-icoon buiten de wielrand.
 */
interface MandalaLocksKeysLockMarkerView {
  readonly id: string;
  readonly gate: number;
  readonly framePathD: string;
  readonly pointerPathD: string;
  readonly iconX: number;
  readonly iconY: number;
}

/**
 * Key-marker: venster om de key-poort + sleutel-icoon buiten de rand. In de
 * chart-scope krijgt een niet-geactiveerde key géén venster — enkel een subtiel
 * (greyed) sleuteltje zodat zichtbaar blijft dát het een key-poort is. De wig
 * (bijstuurronde 1) verschijnt bij een venster én — gedempt — wanneer de
 * keys-ster aan staat, zodat de ster-vierkanten altijd een wigpunt hebben.
 */
interface MandalaLocksKeysKeyMarkerView {
  readonly id: string;
  readonly gate: number;
  readonly lockGate: number;
  readonly activated: boolean;
  readonly framePathD: string | null;
  readonly pointerPathD: string | null;
  readonly iconX: number;
  readonly iconY: number;
}

/**
 * Ster-vierkant van de 8-puntige ster (bijstuurronde 1): wig-tot-wig over de
 * vier locks of keys van één incarnatiekruis. De locks-ster kleurt per
 * lock-kruis; de keys-ster per arm (vierkant van het lentepunt fel, het andere
 * gedempt), zodat de kleur bij een lock-fase met de arm meereist.
 */
interface MandalaLocksKeysStarView {
  readonly id: string;
  readonly kind: 'locks-vessel' | 'locks-sphinx' | 'keys-equinox' | 'keys-counter';
  readonly pathD: string;
}

/**
 * Rode wijzer van het lentepunt (de precessie-equinox): een lijn vanuit het
 * wielcentrum naar de key-wig van de lentepunt-poort, met een stip op beide
 * uiteinden. Enkel zichtbaar zodra een ster aan staat.
 */
interface MandalaLocksKeysEquinoxPointerView {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

interface MandalaLocksKeysConnectorView {
  readonly id: string;
  readonly lockGate: number;
  readonly keyGate: number;
  readonly pathD: string;
}

/**
 * Gebogen keynote (de rol van de lock, bv. "De Weg" / "De Tempel") die als
 * cirkeltekst boven het slotje hangt, net buiten de wielrand. Volgt dezelfde
 * boog- en leesrichtingregels als de kwartaal-keynotes: enkel op de onderste
 * halve cirkel keert de boog om zodat de tekst overal rechtop blijft.
 */
interface MandalaLocksKeysKeynoteView {
  readonly id: string;
  readonly text: string;
  readonly arcId: string;
  readonly arcD: string;
}

/**
 * Procedureel getekend Human Design Mandala-wiel (64 poorten × 6 lijnen) als SVG.
 *
 * Geometrie + segmenten komen uit `mandala-layout.ts` (afgeleid uit de
 * poortvolgorde + graden-anker). Dit component voegt enkel data-gedreven
 * inkleuring (centerkleur per geactiveerde poort), de groene lijn-vakjes
 * (activatie tot op lijn-niveau) en de hover-koppeling met de bodygraph toe.
 */
@Component({
  selector: 'hd-mandala-wheel',
  imports: [SharedModule],
  templateUrl: './mandala-wheel.component.html',
  styleUrl: './mandala-wheel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--hd-moon-transit-color]': 'moonTransitColor()',
    '[style.--hd-just-now-transit-color]': 'justNowTransitColor',
    '[style.--hd-locks-keys-lock-color]': 'locksKeysLockColor()',
    // Uniforme wiel-schaal: de SCSS vermenigvuldigt alle fontgroottes hiermee
    // zodat cijfers/glyphs/keynotes exact meeschalen met de ring-radii.
    '[style.--mandala-wheel-scale]': 'wheelScale()',
  },
})
export class MandalaWheelComponent {
  readonly configuration = input.required<HdBodygraphConfig>();
  private readonly activePalette = inject(HdActivePaletteService);
  /** Kleurenpalet; zonder binding (miniaturen, posters) volgt dit het actieve palet van de gebruiker. */
  readonly colorConfigInput = input<HdBodygraphColorConfig | undefined>(undefined, { alias: 'colorConfig' });
  readonly colorConfig = computed(() => this.colorConfigInput() ?? this.activePalette.colorConfig());
  readonly planetaryActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  readonly geometry = input<MandalaGeometryConfig>(DEFAULT_MANDALA_GEOMETRY);
  readonly showZodiacRing = input(false);
  readonly showQuarterRing = input(false);
  readonly showIchingRing = input(false);
  /** Godhead-band: binnen de Ring Pass Not, tussen poortenring en dierenriem. */
  readonly showGodheadRing = input(false);

  /**
   * Eén-info-ring-modus (telefoon, docs/MOBIEL.md §9 besluit 2): toont van de
   * vier informatieve ringen enkel de gekozen; de kern (poorten, lijnen,
   * labels, activatie-zone en de transit-/locks&keys-lagen) blijft altijd
   * staan. Overstemt de losse `show*Ring`-inputs. De page geeft dezelfde
   * waarde als `singleInfoRing`-optie aan `buildMandalaWheelLayout` mee zodat
   * de geometrie meebeweegt: de gekozen ring ligt dan op de buitenrand (óók
   * de dierenriem/godheads) en de kern groeit op. `null` = gewone weergave.
   */
  readonly singleInfoRing = input<MandalaInfoRingKey | null>(null);

  /**
   * Centerkleur gefaded doortrekken in de slices van geactiveerde poorten
   * (zelfde kleur als het poortvakje, volgens het ingestelde center-
   * kleurenschema). Visualisatie-setting; uit = enkel de neutrale spaken.
   */
  readonly showSliceCenterFill = input(false);
  /**
   * PDF-print: de gefadede slice-vulling als dekkende kleur (vooraf op wit gemengd) i.p.v. via
   * opacity. Printerdrivers vlakken PDF-transparantie slecht af; op wit papier oogt het gelijk.
   */
  readonly sliceCenterFillOpaque = input(false);

  /**
   * Planeetactivatie-glyphs voor de activatie-zone: per poort gestapeld in de
   * slice, van de wielrand naar binnen toe. Kleuren zijn door de page al
   * opgelost naar de bron-semantiek van de actieve weergave.
   */
  readonly gateGlyphs = input<readonly HdMandalaGateGlyph[]>([]);

  /** Kleurvariant van de kwartaalband (settings-panel: zacht/vol A/B-test). */
  readonly quarterColorVariant = input<HdMandalaQuarterColorVariant>('soft');
  /** Dierenriem-band: donker (default, witte glyphs) of licht (donkere glyphs). */
  readonly zodiacRingVariant = input<HdMandalaZodiacRingVariant>('dark');

  /** Gelokaliseerde kwartaal-keynote-delen; null = Engelse canon uit de constants. */
  readonly quarterTexts = input<HdMandalaQuarterTexts | null>(null);

  /** Gelokaliseerde dierenriem-namen (native tooltip); null = namen uit de constants. */
  readonly zodiacSignNames = input<HdMandalaZodiacNames | null>(null);

  /** Tooltips van de seizoensmarkeringen en kwartaalbanden; null = markeringen zonder tooltip. */
  readonly seasonTexts = input<HdMandalaSeasonTexts | null>(null);

  /** Seizoens-badges tonen (PDF-print zet dit uit: daar is geen ruimte buiten de wielrand gereserveerd). */
  readonly showSeasonMarkers = input(true);

  /**
   * Maan-transit viewdata (spec 2026-07-06 §6.2): rings om de formatie-poorten,
   * verbindingslijnen naar de partnerpoorten en de today-marker. `null` =
   * transitlaag niet renderen.
   */
  readonly moonTransit = input<HdMandalaMoonTransitViewData | null>(null);
  readonly justNowTransit = input<HdMandalaJustNowTransitViewData | null>(null);

  /**
   * Locks & Keys viewdata (spec 2026-08-27): gele vensters om de acht
   * G-poorten (locks), dusk-blauwe markers om de key-poorten van de gekozen
   * periode en verbindingslijnen key → lock. `null` = laag niet renderen.
   */
  readonly locksKeys = input<HdMandalaLocksKeysViewData | null>(null);

  /**
   * Gelokaliseerde lock-keynotes (rol per lock-poort, bv. `{ 25: 'De Weg' }`)
   * die als cirkeltekst boven de slotjes verschijnen. `null`/ontbrekende poort
   * = geen keynote voor die lock. Enkel zinvol samen met {@link locksKeys}.
   */
  readonly locksKeysKeynotes = input<Readonly<Record<number, string>> | null>(null);

  /**
   * Poort-patronen-overlay (Mandala-tab in de chart-editor): kanaal-links,
   * partner-links en het incarnatiekruis. `null` = laag niet renderen.
   */
  readonly patterns = input<HdMandalaPatternsViewData | null>(null);

  /**
   * Klik op een lock-venster, slot-icoon of key-icoon in de Locks & Keys-laag:
   * de page opent het detailpaneel van de bijhorende lock.
   */
  readonly locksKeysLockClicked = output<number>();

  /**
   * In de maan-transit modus staan hover-effecten uit (rustiger beeld,
   * performanter; de card-klik is daar de primaire interactie). Schakelt zowel
   * de service-koppeling als de lokale wiel-hover uit.
   */
  readonly hoverEffectsEnabled = input(true);

  /**
   * ChannelKeys van formaties die gehighlight worden (hover op card, connector
   * of maanpoort — finetune-ronde 2): hun connector gaat naar volle opacity.
   */
  readonly moonTransitHighlightedKeys = input<ReadonlySet<string>>(new Set<string>());

  /**
   * Hover op een máánpoort-segment terwijl de gewone hover-effecten uit staan:
   * de page highlight dan de bijhorende card(s) + connector(s) en het
   * kanaalstuk in de bodygraph. `null` = hover weg of geen maanpoort.
   */
  readonly moonTransitGateHovered = output<number | null>();

  /** Hover op een connector-lijn: zelfde highlight-flow als card-hover. */
  readonly moonTransitConnectorHovered = output<HdMoonTransitFormation | null>();

  /** Klik op een connector-lijn: opent de kanaal-info dialog (als card-klik). */
  readonly moonTransitConnectorClicked = output<HdMoonTransitFormationClickEvent>();
  readonly justNowTransitHighlightedKeys = input<ReadonlySet<string>>(new Set<string>());
  readonly justNowTransitGateHovered = output<number | null>();
  readonly justNowTransitConnectorHovered = output<HdJustNowTransitFormation | null>();
  readonly justNowTransitConnectorClicked = output<HdJustNowTransitFormationClickEvent>();

  /**
   * Wanneer uitgeschakeld (debug-knop) laten we de native browser-context-menu
   * door op rechterklik, net als de bodygraph-chart (handig voor DevTools).
   */
  readonly contextMenuEnabled = input(true);

  /**
   * Bepaalt — identiek aan de bodygraph-chart — wat een linkermuisklik op een
   * poort doet. Bij `'cycle'` + `primaryGateAction === 'configure'` (edit-modus)
   * wordt de poort ge-cycled via {@link configurationChange}; bij
   * `'show-info'` (info-modus) wordt het gate-info-paneel geopend.
   */
  readonly interactionMode = input<HdBodygraphInteractionMode>('cycle');
  readonly primaryGateAction = input<HdGatePrimaryAction>('configure');

  /**
   * Klik op een poort (poort-cel of bijbehorend lijn-vakje) in het wiel.
   * Wordt op de chart-pagina aan dezelfde `handleGateClicked`-handler gekoppeld
   * als een klik op de poort in de bodygraph-chart of de planeetgrid.
   */
  readonly gateClicked = output<number>();

  /**
   * Nieuwe configuratie na een poort-cycle in edit-modus, identiek aan de
   * bodygraph-chart (`onConfigurationChange`).
   */
  readonly configurationChange = output<HdBodygraphConfig>();

  /**
   * Linkermuisklik op een poort: opent het gate-info-paneel, identiek aan een
   * klik op de poort in de bodygraph-chart of planeetgrid
   * (`handleGateInfoRequested`).
   */
  readonly gateInfoRequested = output<{ gate: number; origin: { x: number; y: number } }>();

  /**
   * Rechtermuisklik op een poort: opent het contextmenu op de cursor, identiek
   * aan de bodygraph-chart (`handleGateContextMenuRequested`).
   */
  readonly gateContextMenuRequested = output<{ gate: number; position: { x: number; y: number } }>();

  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly destroyRef = inject(DestroyRef);

  /** Lokale hover-state zodat het wiel ook losstaand (zonder actieve service) oplicht. */
  private readonly hoveredGate = signal<number | null>(null);
  private readonly hoveredLine = signal<string | null>(null);

  /**
   * Gedeelde debounce-timer voor de LOKALE hover-signalen. De
   * `hoverService` debounced zijn eigen state al (zie
   * `HD_HOVER_ENTER_DELAY_MS`/`LEAVE`), maar de mandala houdt daarnaast een
   * lokale `hoveredGate`/`hoveredLine` bij die tot nu toe synchroon werd gezet
   * — wat bij snel slepen flikkering gaf. Eén gedeelde timer (true debounce)
   * spiegelt de service: elke nieuwe enter/leave annuleert de vorige pending
   * actie, zodat enkel de laatste landt na de delay.
   */
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.cancelHoverTimer());
  }

  /**
   * Uniforme wiel-schaal t.o.v. de basisgeometrie: 1 op de kern-basismaat
   * (poortenring outer = {@link MOON_TRANSIT_WHEEL_EDGE_RADIUS}), > 1 wanneer
   * verborgen buitenringen ruimte teruggeven, < 1 in geklemde transit-modi.
   * Schaalt de fontgroottes (via de host-CSS-variabele) en de glyph-metrics
   * mee met de radii.
   */
  protected readonly wheelScale = computed(
    () => this.geometry().gateRing.outerRadius / MOON_TRANSIT_WHEEL_EDGE_RADIUS,
  );

  protected readonly viewBoxSize = computed(() => this.geometry().viewBoxSize);
  protected readonly viewBox = computed(() => {
    const size = this.geometry().viewBoxSize;
    return `0 0 ${size} ${size}`;
  });
  protected readonly center = computed(() => this.geometry().center);

  /**
   * Effectieve ringzichtbaarheid: in de één-ring-modus wint
   * {@link singleInfoRing} van de losse toggles (enkel de gekozen band
   * rendert); met `null` gelden de `show*Ring`-inputs ongewijzigd.
   */
  protected readonly quarterRingVisible = computed(() => {
    const single = this.singleInfoRing();
    return single != null ? single === 'quarters' : this.showQuarterRing();
  });
  protected readonly godheadRingVisible = computed(() => {
    const single = this.singleInfoRing();
    return single != null ? single === 'godheads' : this.showGodheadRing();
  });
  protected readonly zodiacRingVisible = computed(() => {
    const single = this.singleInfoRing();
    return single != null ? single === 'zodiac' : this.showZodiacRing();
  });
  protected readonly ichingRingVisible = computed(() => {
    const single = this.singleInfoRing();
    return single != null ? single === 'iching' : this.showIchingRing();
  });

  /**
   * Binnenrand van het zichtbaar getekende wiel. De dierenriem-ring ligt ín het
   * wiel — direct binnen de poortenring (2026-08-16) — dus zodra hij aan staat
   * is zíjn binnenstraal de rand van het binnengat. De center-disc en de
   * transit-connectors hangen hieraan, zodat de disc de dierenriem niet
   * overdekt en connectors nooit over een ringband lopen. In de één-ring-modus
   * liggen álle infobanden buiten de kern (ook dierenriem/godheads): het
   * binnengat begint dan altijd op de poortenring.
   */
  protected readonly innerWheelRadius = computed(() => {
    const geometry = this.geometry();
    if (this.singleInfoRing() == null) {
      if (this.showZodiacRing()) {
        return geometry.zodiacRing.innerRadius;
      }
      if (this.showGodheadRing()) {
        return geometry.godheadRing.innerRadius;
      }
    }
    return geometry.gateRing.innerRadius;
  });

  /**
   * Binnenstraal van de activatie-zone: samen met {@link innerWheelRadius} de
   * band waarin de slice-spaken lopen en waar een volgende update de
   * planeetactivatie-glyphs per poort naar binnen toe stapelt. Gekoppeld aan
   * de poortenring zodat de zone met de uniforme wiel-schaal meebeweegt.
   */
  protected readonly sliceInnerRadius = computed(
    () => this.geometry().gateRing.innerRadius * MANDALA_GATE_SLICE_INNER_RADIUS_FACTOR,
  );

  /**
   * Pizza-slices achter de chart: per poort een radiale spaak die de
   * poortgrens vanaf de binnenrand van het wiel doortrekt tot de
   * activatie-zone-rand. Elke poort krijgt zo zijn eigen taartpunt waarin de
   * planeetactivaties (volgende update) een plek hebben.
   */
  protected readonly sliceViews = computed<MandalaSliceView[]>(() => {
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const outerRadius = this.innerWheelRadius();
    const innerRadius = this.sliceInnerRadius();
    return buildMandalaGateSegments(geometry).map(segment => ({
      id: `${segment.id}-slice`,
      pathD: describeSpoke(cx, cy, innerRadius, outerRadius, segment.startAngle),
    }));
  });

  /**
   * Gefadede centerkleur-vulling van de slices met minstens één activatie
   * (visualisatie-setting): exact dezelfde kleurbron als het poortvakje in de
   * ring — {@link HdBodygraphColorConfig.centerDefined} per center van de
   * poort — maar op lage dekking (SCSS) als subtiel doortrek-effect tussen
   * wielrand en zone-rand.
   */
  protected readonly sliceCenterFillViews = computed<MandalaSliceCenterFillView[]>(() => {
    if (!this.showSliceCenterFill()) {
      return [];
    }
    const geometry = this.geometry();
    const colors = this.colorConfig();
    const active = this.activeGateSet();
    const opaque = this.sliceCenterFillOpaque();
    const { x: cx, y: cy } = geometry.center;
    const outerRadius = this.innerWheelRadius();
    const innerRadius = this.sliceInnerRadius();
    return buildMandalaGateSegments(geometry).flatMap(segment => {
      const center = HD_BODYGRAPH_GATE_TO_CENTER[segment.gate];
      if (!active.has(segment.gate) || center == null) {
        return [];
      }
      const color = colors.centerDefined[center];
      return [{
        id: `${segment.id}-slice-fill`,
        gate: segment.gate,
        pathD: describeAnnularSector(cx, cy, innerRadius, outerRadius, segment.startAngle, segment.endAngle),
        fill: opaque ? blendOver(color, 'rgb(255,255,255)', MANDALA_SLICE_CENTER_FILL_OPACITY) : color,
        opacity: opaque ? 1 : MANDALA_SLICE_CENTER_FILL_OPACITY,
      }];
    });
  });

  /**
   * Planeetactivatie-glyphs, per poort radiaal gestapeld in de slice: de
   * eerste glyph hangt net binnen de wielrand (met de dierenriem áán start hij
   * automatisch onder de dierenriem-band — {@link innerWheelRadius}), elke
   * volgende stapt {@link HD_MANDALA_GLYPH_STEP} naar binnen. Een stapel die
   * dieper zou reiken dan de zone-rand wordt samengedrukt zodat alles binnen
   * de activatie-zone blijft.
   */
  protected readonly glyphViews = computed<MandalaGlyphView[]>(() => {
    const glyphs = this.gateGlyphs();
    if (glyphs.length === 0) {
      return [];
    }
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const segments = this.gateSegmentByGate();
    // Glyph-fontgrootte schaalt mee via de SCSS-var; de stapel-metrics volgen.
    const wheelScale = this.wheelScale();
    const glyphFontSize = HD_MANDALA_GLYPH_FONT_SIZE * wheelScale;
    const glyphStep = HD_MANDALA_GLYPH_STEP * wheelScale;
    const outerCenter = this.innerWheelRadius() - HD_MANDALA_GLYPH_EDGE_PADDING * wheelScale - glyphFontSize / 2;
    const minCenter = this.sliceInnerRadius() + glyphFontSize / 2;

    const byGate = new Map<number, HdMandalaGateGlyph[]>();
    for (const glyph of glyphs) {
      const list = byGate.get(glyph.gate) ?? [];
      list.push(glyph);
      byGate.set(glyph.gate, list);
    }

    const views: MandalaGlyphView[] = [];
    for (const [gate, stack] of byGate) {
      const segment = segments.get(gate);
      if (segment == null) {
        continue;
      }
      const step = stack.length > 1
        ? Math.min(glyphStep, (outerCenter - minCenter) / (stack.length - 1))
        : 0;
      stack.forEach((glyph, index) => {
        const radius = outerCenter - index * step;
        const position = polarToCartesian(cx, cy, radius, segment.midAngle);
        views.push({
          key: glyph.key,
          gate,
          glyph: glyph.glyph,
          color: glyph.color,
          x: position.x,
          y: position.y,
        });
      });
    }
    return views;
  });

  /** Poorten met minstens één activatie → krijgen hun centerkleur. */
  private readonly activeGateSet = computed<ReadonlySet<number>>(() => {
    const gates = new Set<number>();
    for (const activation of this.configuration().activations) {
      gates.add(activation.gate);
    }
    return gates;
  });

  /** `gate*10+line` van elke planetaire activatie → groen lijn-vakje. */
  private readonly activeLineSet = computed<ReadonlySet<number>>(() => {
    const lines = new Set<number>();
    for (const activation of this.planetaryActivations()) {
      lines.add(activation.gate * 10 + activation.line);
    }
    return lines;
  });

  protected readonly gateViews = computed<MandalaGateView[]>(() => {
    const geometry = this.geometry();
    const colors = this.colorConfig();
    const active = this.activeGateSet();
    const { x: cx, y: cy } = geometry.center;
    const labelRadius = (geometry.gateRing.innerRadius + geometry.lineRing.innerRadius) / 2;
    return buildMandalaGateSegments(geometry).map(segment => {
      const center = HD_BODYGRAPH_GATE_TO_CENTER[segment.gate];
      const isActive = active.has(segment.gate);
      const fill = isActive && center != null
        ? colors.centerDefined[center]
        : (colors.centerOpen ?? 'rgb(249,247,241)');
      const label = polarToCartesian(cx, cy, labelRadius, segment.midAngle);
      return {
        id: segment.id,
        gate: segment.gate,
        pathD: segment.pathD,
        fill,
        isActive,
        labelX: label.x,
        labelY: label.y,
        labelFill: isHdBodygraphDarkCenterColor(fill) ? resolveHdBodygraphCenterTextColor(fill) : undefined,
      };
    });
  });

  protected readonly lineViews = computed<MandalaLineView[]>(() => {
    const geometry = this.geometry();
    const activeLines = this.activeLineSet();
    return buildMandalaLineSegments(geometry).map(segment => ({
      id: segment.id,
      gate: segment.gate,
      line: segment.line,
      pathD: segment.pathD,
      isActive: activeLines.has(segment.gate * 10 + segment.line),
    }));
  });

  protected readonly zodiacViews = computed<MandalaZodiacView[]>(() => {
    const geometry = this.geometry();
    const names = this.zodiacSignNames();
    const { x: cx, y: cy } = geometry.center;
    const glyphRadius = (geometry.zodiacRing.innerRadius + geometry.zodiacRing.outerRadius) / 2;
    return buildMandalaZodiacSegments(geometry).map(segment => {
      const glyph = polarToCartesian(cx, cy, glyphRadius, segment.midAngle);
      return {
        id: segment.id,
        key: segment.key,
        name: names?.[segment.key] ?? segment.name,
        glyph: segment.glyph,
        pathD: segment.pathD,
        glyphX: glyph.x,
        glyphY: glyph.y,
      };
    });
  });

  /**
   * Kwartaal-segmenten met hun keynote als gebogen tekst (textPath). De boog
   * volgt de cirkel op het bandmidden; drie kwartalen lezen met de klok mee
   * (boven → recht, links → 90° CCW gekanteld, rechts → 90° CW), enkel het
   * ónderste kwartaal krijgt een omgekeerde boog zodat de tekst daar rechtop
   * staat en aan de uiteinden naar boven afbuigt.
   */
  protected readonly quarterViews = computed<MandalaQuarterView[]>(() => {
    const geometry = this.geometry();
    const texts = this.quarterTexts();
    const { x: cx, y: cy } = geometry.center;
    const midRadius = (geometry.quarterRing.innerRadius + geometry.quarterRing.outerRadius) / 2;
    // De fontgrootte schaalt mee met het wiel (SCSS-var); de baseline-
    // compensatie moet dezelfde effectieve grootte gebruiken.
    const fontSize = HD_MANDALA_QUARTER_KEYNOTE_FONT_SIZE * this.wheelScale();
    return buildMandalaQuarterSegments(geometry).map(segment => {
      const isBottomQuarter = isBottomHalfAngle(segment.midAngle);
      // Baseline-compensatie: op een gewone boog groeien de glyphs naar búiten
      // (baseline iets naar binnen leggen), op de omgekeerde onderboog naar
      // bínnen (baseline iets naar buiten leggen).
      const baselineRadius = midRadius + (isBottomQuarter ? 1 : -1) * fontSize * 0.35;
      const from = polarToCartesian(cx, cy, baselineRadius, isBottomQuarter ? segment.endAngle : segment.startAngle);
      const to = polarToCartesian(cx, cy, baselineRadius, isBottomQuarter ? segment.startAngle : segment.endAngle);
      const sweepFlag = isBottomQuarter ? 0 : 1;
      const title = texts?.[segment.key]?.title ?? segment.title;
      const purpose = texts?.[segment.key]?.purpose ?? segment.purpose;
      return {
        tooltip: this.seasonTexts()?.quarters[segment.key] ?? null,
        id: segment.id,
        key: segment.key,
        label: segment.label,
        keynoteLead: `${title} · `,
        keynotePurpose: purpose,
        keynoteTail: ` · ${segment.star}`,
        pathD: segment.pathD,
        arcId: `${segment.id}-keynote-arc`,
        arcD: `M ${from.x} ${from.y} A ${baselineRadius} ${baselineRadius} 0 0 ${sweepFlag} ${to.x} ${to.y}`,
      };
    });
  });

  /**
   * Godhead-segmenten met hun naam als gebogen tekst — zelfde boog- en
   * leesrichting-regels als de kwartaal-keynotes (enkel het onderste venster
   * krijgt een omgekeerde boog).
   */
  protected readonly godheadViews = computed<MandalaGodheadView[]>(() => {
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const midRadius = (geometry.godheadRing.innerRadius + geometry.godheadRing.outerRadius) / 2;
    const fontSize = HD_MANDALA_GODHEAD_FONT_SIZE * this.wheelScale();
    return buildMandalaGodheadSegments(geometry).map(segment => {
      const isBottomSegment = isLowerSemicircleAngle(segment.midAngle);
      const baselineRadius = midRadius + (isBottomSegment ? 1 : -1) * fontSize * 0.35;
      const from = polarToCartesian(cx, cy, baselineRadius, isBottomSegment ? segment.endAngle : segment.startAngle);
      const to = polarToCartesian(cx, cy, baselineRadius, isBottomSegment ? segment.startAngle : segment.endAngle);
      const sweepFlag = isBottomSegment ? 0 : 1;
      return {
        id: segment.id,
        key: segment.key,
        name: segment.name,
        quarterKey: segment.quarterKey,
        pathD: segment.pathD,
        arcId: `${segment.id}-name-arc`,
        arcD: `M ${from.x} ${from.y} A ${baselineRadius} ${baselineRadius} 0 0 ${sweepFlag} ${to.x} ${to.y}`,
      };
    });
  });

  protected readonly ichingViews = computed<MandalaIchingView[]>(() => {
    const geometry = this.geometry();
    return buildMandalaIchingSymbols(geometry).map(symbol => ({
      id: symbol.id,
      gate: symbol.gate,
      glyph: getHdGateIchingChar(symbol.gate),
      glyphX: symbol.glyphX,
      glyphY: symbol.glyphY,
    }));
  });

  /** Transitkleur = het circuit-highlight-blauw uit de kleurconfig (spec §6.2). */
  protected readonly moonTransitColor = computed(() => this.colorConfig().channelHighlight);
  protected readonly justNowTransitColor = HD_JUST_NOW_TRANSIT_COLOR;

  /** Segment-lookup per poort voor de transit-lagen (midAngle-bron). */
  private readonly gateSegmentByGate = computed<ReadonlyMap<number, MandalaGateSegment>>(
    () => new Map(buildMandalaGateSegments(this.geometry()).map(segment => [segment.gate, segment])),
  );

  /**
   * Eén markering per uniek maanpoort met minstens één formatie. Stijl `ring`:
   * een cirkel gecentreerd op het póórtnummer (label-radius) met een compacte
   * straal (finetune 2026-07-06). Stijl `frame`: een venster-kader dat exact op
   * de natuurlijke grenzen van het poortsegment valt — hergebruikt `segment.pathD`,
   * dus geen uitstulping; de rand ligt óp het segment, niet erbuiten.
   */
  protected readonly moonGateMarkerViews = computed<MandalaMoonGateMarkerView[]>(() => {
    const transit = this.moonTransit();
    if (transit == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    // Zelfde radius als waar gateViews het poortnummer plaatst.
    const ringMidRadius = (geometry.gateRing.innerRadius + geometry.lineRing.innerRadius) / 2;
    const radius = (geometry.lineRing.innerRadius - geometry.gateRing.innerRadius) / 2 + 4;
    const seen = new Set<number>();
    const views: MandalaMoonGateMarkerView[] = [];
    for (const formation of transit.formations) {
      if (seen.has(formation.moonGate)) {
        continue;
      }
      seen.add(formation.moonGate);
      const segment = segments.get(formation.moonGate);
      if (segment == null) {
        continue;
      }
      if (transit.gateMarkerStyle === 'frame') {
        // Hergebruik het exacte poortsegment-pad zodat de kaderrand samenvalt met
        // de natuurlijke segmentgrenzen (geen uitstulping, niet groter dan de poort).
        views.push({ id: `moon-frame-${formation.moonGate}`, kind: 'frame', pathD: segment.pathD });
      } else {
        const position = polarToCartesian(cx, cy, ringMidRadius, segment.midAngle);
        views.push({ id: `moon-ring-${formation.moonGate}`, kind: 'ring', cx: position.x, cy: position.y, r: radius });
      }
    }
    return views;
  });

  /** Verbindingslijn per formatie, recht of licht naar binnen gebogen (§6.2). */
  protected readonly moonConnectorViews = computed<MandalaMoonConnectorView[]>(() => {
    const transit = this.moonTransit();
    if (transit == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const anchorRadius = this.innerWheelRadius() - 2;
    return transit.formations.flatMap(formation => {
      const moonSegment = segments.get(formation.moonGate);
      const partnerSegment = segments.get(formation.partnerGate);
      if (moonSegment == null || partnerSegment == null) {
        return [];
      }
      const from = polarToCartesian(cx, cy, anchorRadius, moonSegment.midAngle);
      const to = polarToCartesian(cx, cy, anchorRadius, partnerSegment.midAngle);
      const pathD = transit.connectorStyle === 'straight'
        ? `M ${from.x} ${from.y} L ${to.x} ${to.y}`
        : describeInwardCurve(from, to, cx, cy, moonSegment.midAngle, partnerSegment.midAngle);
      return [{
        id: `moon-connector-${formation.channelKey}`,
        channelKey: formation.channelKey,
        formation,
        pathD,
        endX: to.x,
        endY: to.y,
      }];
    });
  });

  // ── Poort-patronen-overlay (Mandala-tab) ──────────────────────────────────
  // Kanaal- en partner-links komen uit de vaste poort-metadata; de kruisen uit de
  // door de page geleverde paren. Zelfde geometrie als de transit-connectoren:
  // anker op de binnenwielrand, midAngle per poort. Op een geladen chart tonen de
  // kanaal-/partner-links enkel verbindingen die vertrekken vanuit een actieve poort.

  private readonly patternAnchorRadius = computed(() => this.innerWheelRadius() - 2);

  /**
   * Bouwt één patronen-connector tussen twee poorten. `curved` = gebogen koorde
   * (kanalen), anders een rechte lijn (partners/kruis-diameters). `null` wanneer
   * een van de poortsegmenten ontbreekt.
   */
  private buildPatternConnector(
    idPrefix: string,
    gateA: number,
    gateB: number,
    curved: boolean,
  ): MandalaPatternConnectorView | null {
    const segments = this.gateSegmentByGate();
    const segA = segments.get(gateA);
    const segB = segments.get(gateB);
    if (segA == null || segB == null) return null;
    const { x: cx, y: cy } = this.geometry().center;
    const anchor = this.patternAnchorRadius();
    const from = polarToCartesian(cx, cy, anchor, segA.midAngle);
    const to = polarToCartesian(cx, cy, anchor, segB.midAngle);
    const pathD = curved
      ? describeInwardCurve(from, to, cx, cy, segA.midAngle, segB.midAngle)
      : `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
    return { id: `${idPrefix}-${gateA}-${gateB}`, pathD, gateA, gateB };
  }

  /**
   * Filtert kanaal-/partner-links op een geladen chart: enkel verbindingen die
   * vertrekken vanuit een actieve poort. Op een lege chart (geen activaties)
   * toont de volledige structuur.
   */
  private patternIncludesGatePair(gateA: number, gateB: number): boolean {
    const active = this.activeGateSet();
    if (active.size === 0) return true;
    return active.has(gateA) || active.has(gateB);
  }

  /** Kanaal-links: gebogen koorden tussen de twee poorten van elk kanaal. */
  protected readonly patternChannelConnectorViews = computed<MandalaPatternConnectorView[]>(() => {
    const data = this.patterns();
    if (data == null || !data.showChannelLinks) return [];
    return HD_BODYGRAPH_CHANNELS.flatMap(channel => {
      const [gateA, gateB] = channel.gates;
      if (!this.patternIncludesGatePair(gateA, gateB)) return [];
      const view = this.buildPatternConnector('pattern-channel', gateA, gateB, true);
      return view ? [view] : [];
    });
  });

  /** Partner-links: elke poort naar haar programmeringspartner — rechte diameter. */
  protected readonly patternPartnerConnectorViews = computed<MandalaPatternConnectorView[]>(() => {
    const data = this.patterns();
    if (data == null || !data.showPartnerLinks) return [];
    const views: MandalaPatternConnectorView[] = [];
    for (let gate = 1; gate <= 64; gate++) {
      const partner = getHdGateMetadata(gate).primaryPartnerGate;
      if (partner === 0 || gate >= partner) continue; // elk paar één keer (a < b)
      if (!this.patternIncludesGatePair(gate, partner)) continue;
      const view = this.buildPatternConnector('pattern-partner', gate, partner, false);
      if (view) views.push(view);
    }
    return views;
  });

  private buildCrossConnectors(
    pairs: readonly (readonly [number, number])[] | undefined,
    idPrefix: string,
  ): MandalaPatternConnectorView[] {
    if (pairs == null || pairs.length === 0) return [];
    return pairs.flatMap(pair => {
      const view = this.buildPatternConnector(idPrefix, pair[0], pair[1], false);
      return view ? [view] : [];
    });
  }

  private buildCrossMarkers(
    pairs: readonly (readonly [number, number])[] | undefined,
    idPrefix: string,
  ): MandalaPatternMarkerView[] {
    if (pairs == null || pairs.length === 0) return [];
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const segments = this.gateSegmentByGate();
    const ringMidRadius = (geometry.gateRing.innerRadius + geometry.lineRing.innerRadius) / 2;
    const radius = (geometry.lineRing.innerRadius - geometry.gateRing.innerRadius) / 2 + 4;
    const gates = new Set<number>();
    for (const pair of pairs) {
      gates.add(pair[0]);
      gates.add(pair[1]);
    }
    const views: MandalaPatternMarkerView[] = [];
    for (const gate of gates) {
      const segment = segments.get(gate);
      if (segment == null) continue;
      const position = polarToCartesian(cx, cy, ringMidRadius, segment.midAngle);
      views.push({ id: `${idPrefix}-${gate}`, cx: position.x, cy: position.y, r: radius });
    }
    return views;
  }

  /** Incarnatiekruis-assen (Zon↔Aarde per zijde) van de geladen chart — rechte diameters. */
  protected readonly patternCrossConnectorViews = computed<MandalaPatternConnectorView[]>(
    () => this.buildCrossConnectors(this.patterns()?.incarnationCrossPairs, 'pattern-cross'),
  );

  /** Ring-markeringen op de incarnatiekruis-poorten. */
  protected readonly patternCrossGateMarkerViews = computed<MandalaPatternMarkerView[]>(
    () => this.buildCrossMarkers(this.patterns()?.incarnationCrossPairs, 'pattern-cross-marker'),
  );

  /** Life cross-assen (Noordknoop↔Zuidknoop per zijde) van de geladen chart. */
  protected readonly patternLifeCrossConnectorViews = computed<MandalaPatternConnectorView[]>(
    () => this.buildCrossConnectors(this.patterns()?.lifeCrossPairs, 'pattern-life-cross'),
  );

  /** Ring-markeringen op de life cross-poorten. */
  protected readonly patternLifeCrossGateMarkerViews = computed<MandalaPatternMarkerView[]>(
    () => this.buildCrossMarkers(this.patterns()?.lifeCrossPairs, 'pattern-life-cross-marker'),
  );

  /** Hover-highlight: een patronen-lijn licht op zodra één van haar poorten hovert. */
  protected isPatternConnectorHighlighted(connector: MandalaPatternConnectorView): boolean {
    return this.isGateHighlighted(connector.gateA) || this.isGateHighlighted(connector.gateB);
  }

  protected readonly justNowGateMarkerViews = computed<MandalaJustNowGateMarkerView[]>(() => {
    const transit = this.justNowTransit();
    if (transit == null) return [];
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const radiusFromCenter = (geometry.gateRing.innerRadius + geometry.lineRing.innerRadius) / 2;
    const markerRadius = (geometry.lineRing.innerRadius - geometry.gateRing.innerRadius) / 2 + 4;
    const gates = new Set(transit.formations.flatMap(formation => formation.transitGates));
    return [...gates].flatMap(gate => {
      const segment = segments.get(gate);
      if (!segment) return [];
      const position = polarToCartesian(geometry.center.x, geometry.center.y, radiusFromCenter, segment.midAngle);
      return [{ id: `just-now-ring-${gate}`, gate, cx: position.x, cy: position.y, r: markerRadius }];
    });
  });

  /**
   * G-center-geel voor de lock-vensters en -pointers: volgt het actieve
   * center-kleurenpreset (de 'zelf'-kleur), zodat de laag meekleurt met het
   * gekozen schema. De donkere gouden rand eromheen is een vaste SCSS-kleur.
   */
  protected readonly locksKeysLockColor = computed(() => this.colorConfig().centerDefined.zelf);

  protected readonly lockGlyph = HD_MANDALA_LOCK_GLYPH;
  protected readonly keyGlyph = HD_MANDALA_KEY_GLYPH;

  /**
   * De acht lock-vensters: venster-kader op het exacte poortsegment (zelfde
   * mechanisme als de maan-frame, geen uitstulping), een naar binnen wijzende
   * punt in de spie (referentiebeeld "Wheel of 2027") en het slot-icoon net
   * buiten de zichtbare wielrand.
   */
  protected readonly locksKeysLockMarkerViews = computed<MandalaLocksKeysLockMarkerView[]>(() => {
    const data = this.locksKeys();
    if (data == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const pointerLength = HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH * this.wheelScale();
    const iconRadius = this.visibleWheelEdgeRadius() + HD_MANDALA_LOCKS_KEYS_ICON_EDGE_OFFSET;
    return data.pairs.flatMap(pair => {
      const segment = segments.get(pair.lockGate);
      if (segment == null) {
        return [];
      }
      const icon = polarToCartesian(cx, cy, iconRadius, segment.midAngle);
      return [{
        id: `locks-keys-lock-${pair.lockGate}`,
        gate: pair.lockGate,
        framePathD: segment.pathD,
        pointerPathD: describeInwardPointer(cx, cy, segment, pointerLength),
        iconX: icon.x,
        iconY: icon.y,
      }];
    });
  });

  /**
   * Gebogen keynote (rol) boven elk slotje: cirkeltekst op een boog net buiten
   * de slot-iconen, gecentreerd op het lock-midden. Enkel wanneer er
   * {@link locksKeysKeynotes} zijn meegegeven; ontbreekt een lock in de map dan
   * krijgt die geen label. Zelfde leesrichting-regels als de kwartaal-keynotes.
   */
  protected readonly locksKeysKeynoteViews = computed<MandalaLocksKeysKeynoteView[]>(() => {
    const data = this.locksKeys();
    const keynotes = this.locksKeysKeynotes();
    if (data == null || keynotes == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const fontSize = HD_MANDALA_LOCKS_KEYS_KEYNOTE_FONT_SIZE * this.wheelScale();
    const radius = this.visibleWheelEdgeRadius() + HD_MANDALA_LOCKS_KEYS_KEYNOTE_EDGE_OFFSET;
    return data.pairs.flatMap(pair => {
      const text = keynotes[pair.lockGate];
      const segment = segments.get(pair.lockGate);
      if (text == null || text === '' || segment == null) {
        return [];
      }
      // Onderste halve cirkel: boog omkeren zodat de tekst rechtop blijft; de
      // baseline schuift dan iets naar buiten i.p.v. naar binnen (glyph-groei).
      const isLower = isLowerSemicircleAngle(segment.midAngle);
      const baselineRadius = radius + (isLower ? 1 : -1) * fontSize * 0.35;
      const startAngle = segment.midAngle - HD_MANDALA_LOCKS_KEYS_KEYNOTE_HALF_ANGLE;
      const endAngle = segment.midAngle + HD_MANDALA_LOCKS_KEYS_KEYNOTE_HALF_ANGLE;
      const from = polarToCartesian(cx, cy, baselineRadius, isLower ? endAngle : startAngle);
      const to = polarToCartesian(cx, cy, baselineRadius, isLower ? startAngle : endAngle);
      const sweepFlag = isLower ? 0 : 1;
      return [{
        id: `locks-keys-keynote-${pair.lockGate}`,
        text,
        arcId: `locks-keys-keynote-arc-${pair.lockGate}`,
        arcD: `M ${from.x} ${from.y} A ${baselineRadius} ${baselineRadius} 0 0 ${sweepFlag} ${to.x} ${to.y}`,
      }];
    });
  });

  /**
   * Key-markers van de geselecteerde periode: geactiveerde keys (of álle keys
   * in de algemene scope) krijgen een venster + vol sleutel-icoon;
   * niet-geactiveerde keys in de chart-scope enkel een subtiel greyed
   * sleuteltje buiten de rand.
   */
  protected readonly locksKeysKeyMarkerViews = computed<MandalaLocksKeysKeyMarkerView[]>(() => {
    const data = this.locksKeys();
    if (data == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const pointerLength = HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH * this.wheelScale();
    const iconRadius = this.visibleWheelEdgeRadius() + HD_MANDALA_LOCKS_KEYS_ICON_EDGE_OFFSET;
    return data.pairs.flatMap(pair => {
      const segment = segments.get(pair.keyGate);
      if (segment == null) {
        return [];
      }
      const icon = polarToCartesian(cx, cy, iconRadius, segment.midAngle);
      const showPointer = pair.keyActivated || data.showKeysStar;
      return [{
        id: `locks-keys-key-${pair.keyGate}`,
        gate: pair.keyGate,
        lockGate: pair.lockGate,
        activated: pair.keyActivated,
        framePathD: pair.keyActivated ? segment.pathD : null,
        pointerPathD: showPointer ? describeInwardPointer(cx, cy, segment, pointerLength) : null,
        iconX: icon.x,
        iconY: icon.y,
      }];
    });
  });

  /**
   * Verbindingsboog key → lock per geactiveerd paar: een echte cirkelboog van
   * wigpunt naar wigpunt die zacht richting het wielcentrum doorbuigt
   * (bijstuurronde 2) — lock en key liggen altijd 22,5° uit elkaar.
   */
  protected readonly locksKeysConnectorViews = computed<MandalaLocksKeysConnectorView[]>(() => {
    const data = this.locksKeys();
    if (data == null) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const pointerLength = HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH * this.wheelScale();
    return data.pairs.flatMap(pair => {
      if (!pair.keyActivated) {
        return [];
      }
      const keySegment = segments.get(pair.keyGate);
      const lockSegment = segments.get(pair.lockGate);
      if (keySegment == null || lockSegment == null) {
        return [];
      }
      const from = polarToCartesian(cx, cy, keySegment.innerRadius - pointerLength, keySegment.midAngle);
      const to = polarToCartesian(cx, cy, lockSegment.innerRadius - pointerLength, lockSegment.midAngle);
      return [{
        id: `locks-keys-connector-${pair.lockGate}`,
        lockGate: pair.lockGate,
        keyGate: pair.keyGate,
        pathD: describeLocksKeysConnector(from, to, cx, cy),
      }];
    });
  });

  /**
   * De 8-puntige ster als wig-tot-wig vierkanten: per incarnatiekruis-groep
   * één vierkant over de lock-wigpunten (locks-ster) en één over de
   * key-wigpunten (keys-ster) — samen de twee-maal-twee kruisen die de ster
   * vormen. Hoekpunten in wielvolgorde zodat het vierkant netjes sluit.
   */
  protected readonly locksKeysStarViews = computed<MandalaLocksKeysStarView[]>(() => {
    const data = this.locksKeys();
    if (data == null || (!data.showLocksStar && !data.showKeysStar)) {
      return [];
    }
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const { x: cx, y: cy } = geometry.center;
    const pointerLength = HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH * this.wheelScale();
    const tipPoint = (gate: number): { x: number; y: number } | null => {
      const segment = segments.get(gate);
      if (segment == null) {
        return null;
      }
      return polarToCartesian(cx, cy, segment.innerRadius - pointerLength, segment.midAngle);
    };
    const views: MandalaLocksKeysStarView[] = [];
    for (const group of ['vessel', 'sphinx'] as const) {
      const pairs = [...data.pairs]
        .filter(pair => pair.group === group)
        .sort((a, b) => gateSequenceIndex(a.lockGate) - gateSequenceIndex(b.lockGate));
      if (pairs.length < 3) {
        continue;
      }
      if (data.showLocksStar) {
        const points = pairs.map(pair => tipPoint(pair.lockGate)).filter(point => point != null);
        views.push({
          id: `locks-keys-star-locks-${group}`,
          kind: `locks-${group}`,
          pathD: describeClosedPolygon(points),
        });
      }
      if (data.showKeysStar) {
        // Keys staan op een vaste offset vóór hun lock: de lock-volgorde is
        // dus ook de juiste hoekpunt-volgorde voor het keys-vierkant.
        const points = pairs.map(pair => tipPoint(pair.keyGate)).filter(point => point != null);
        views.push({
          id: `locks-keys-star-keys-${group}`,
          kind: group === data.equinoxGroup ? 'keys-equinox' : 'keys-counter',
          pathD: describeClosedPolygon(points),
        });
      }
    }
    return views;
  });

  /**
   * Lentepunt-wijzer: tekent de precessie zelf zodra een ster aan staat. Eindigt
   * op de wigpunt van de lentepunt-poort, waar ook de ster-hoekpunten liggen.
   */
  protected readonly locksKeysEquinoxPointerView = computed<MandalaLocksKeysEquinoxPointerView | null>(() => {
    const data = this.locksKeys();
    if (data == null || (!data.showLocksStar && !data.showKeysStar)) {
      return null;
    }
    const segment = this.gateSegmentByGate().get(data.equinoxGate);
    if (segment == null) {
      return null;
    }
    const { x: cx, y: cy } = this.geometry().center;
    const pointerLength = HD_MANDALA_LOCKS_KEYS_POINTER_LENGTH * this.wheelScale();
    const tip = polarToCartesian(cx, cy, segment.innerRadius - pointerLength, segment.midAngle);
    return { x1: cx, y1: cy, x2: tip.x, y2: tip.y };
  });

  protected onLocksKeysMarkerClick(lockGate: number, event: MouseEvent): void {
    event.stopPropagation();
    this.locksKeysLockClicked.emit(lockGate);
  }

  protected readonly justNowConnectorViews = computed<MandalaJustNowConnectorView[]>(() => {
    const transit = this.justNowTransit();
    if (transit == null) return [];
    const geometry = this.geometry();
    const segments = this.gateSegmentByGate();
    const anchorRadius = this.innerWheelRadius() - 2;
    return transit.formations.flatMap(formation => {
      const fromSegment = segments.get(formation.gates[0]);
      const toSegment = segments.get(formation.gates[1]);
      if (!fromSegment || !toSegment) return [];
      const from = polarToCartesian(geometry.center.x, geometry.center.y, anchorRadius, fromSegment.midAngle);
      const to = polarToCartesian(geometry.center.x, geometry.center.y, anchorRadius, toSegment.midAngle);
      const pathD = transit.connectorStyle === 'straight'
        ? `M ${from.x} ${from.y} L ${to.x} ${to.y}`
        : describeInwardCurve(
            from,
            to,
            geometry.center.x,
            geometry.center.y,
            fromSegment.midAngle,
            toSegment.midAngle,
          );
      return [{
        id: `just-now-connector-${formation.channelKey}`,
        channelKey: formation.channelKey,
        formation,
        pathD,
        endX: to.x,
        endY: to.y,
      }];
    });
  });

  /**
   * Buitenrand van het zichtbaar getekende wiel: de buitenste ring die
   * daadwerkelijk aan staat (de poortenring is er altijd). De today-marker
   * hangt hieraan, zodat hij ook met extra ringen áán netjes buiten het wiel
   * blijft i.p.v. óp de i-tjing-band te belanden. De dierenriem telt niet mee:
   * die ligt ín het wiel (zie {@link innerWheelRadius}) — behálve in de
   * één-ring-modus, waar élke zichtbare infoband buiten de kern ligt en dus
   * de buitenrand bepaalt.
   */
  private readonly visibleWheelEdgeRadius = computed<number>(() => {
    const geometry = this.geometry();
    const edge = geometry.gateRing.outerRadius;
    const single = this.singleInfoRing();
    if (single != null) {
      const infoRings: Readonly<Record<MandalaInfoRingKey, MandalaRingRadii>> = {
        quarters: geometry.quarterRing,
        godheads: geometry.godheadRing,
        zodiac: geometry.zodiacRing,
        iching: geometry.ichingRing,
      };
      return Math.max(edge, infoRings[single].outerRadius);
    }
    let visibleEdge = edge;
    if (this.showIchingRing()) {
      visibleEdge = Math.max(visibleEdge, geometry.ichingRing.outerRadius);
    }
    if (this.showQuarterRing()) {
      visibleEdge = Math.max(visibleEdge, geometry.quarterRing.outerRadius);
    }
    return visibleEdge;
  });

  /**
   * Today-marker (maan-glyph + naald) op de exacte maan-longitude, of null.
   * De naald reikt van net buiten de zichtbare wielrand tot ín de
   * lijn-cellenband en blijft weg uit de nummerzone van de poort (finetune
   * 2026-07-06); het icoon draagt de positie-tooltip (finetune-ronde 3).
   */
  protected readonly todayMarkerView = computed<MandalaMoonTodayView | null>(() => {
    const transit = this.moonTransit();
    if (transit == null || transit.todayLongitude == null) {
      return null;
    }
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const angle = longitudeToScreenAngle(transit.todayLongitude);
    const edge = this.visibleWheelEdgeRadius();
    const inner = polarToCartesian(cx, cy, geometry.lineRing.innerRadius + 1, angle);
    const outer = polarToCartesian(cx, cy, edge + 8, angle);
    const icon = polarToCartesian(cx, cy, edge + MOON_TRANSIT_TODAY_ICON_EDGE_OFFSET, angle);
    return {
      x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y,
      iconX: icon.x, iconY: icon.y, glyph: HD_MANDALA_MOON_GLYPH,
      tooltip: transit.todayTooltip,
    };
  });

  /**
   * Seizoensmarkeringen (lecture 2026-10-21, analyse 4C): de vier cardinale
   * lengtes 0°, 90°, 180° en 270° als rond amber badge-je met zonsymbool. Ze
   * vallen in lijn 2 van de Vessel of Love-poorten 25, 15, 46 en 10, het
   * midden van elk kwartaal, en tonen zich zodra de kwartaal- of
   * dierenriemband aan staat (de banden die de kalender van het wiel
   * vertellen). Met de kwartaalband aan hangt het badge-je volledig búiten de
   * wielrand (de chartpagina reserveert daarvoor `HD_SEASON_MARKER_ROOM` via
   * `maxOuterRadius`, anders zou de viewBox het knippen); met enkel de
   * dierenriem aan staat het midden op die band, op de cusp van Ram, Kreeft,
   * Weegschaal of Steenbok. In de transit- en Locks & Keys-modi, die zelf
   * iconen buiten de rand hangen, blijven de badges weg. De zon is als twee
   * cirkels getekend (geen font-glyph), zodat hij exact gecentreerd staat.
   */
  /** Zonsymbool in het seizoens-badge-je: buitencirkel en kernpunt (template-binding). */
  protected readonly seasonSunRingRadius = MANDALA_SEASON_SUN_RING_RADIUS;
  protected readonly seasonSunDotRadius = MANDALA_SEASON_SUN_DOT_RADIUS;

  protected readonly seasonMarkerViews = computed<MandalaSeasonMarkerView[]>(() => {
    const quarter = this.quarterRingVisible();
    if (!this.showSeasonMarkers() || (!quarter && !this.zodiacRingVisible())) {
      return [];
    }
    if (this.moonTransit() != null || this.justNowTransit() != null || this.locksKeys() != null) {
      return [];
    }
    const geometry = this.geometry();
    const { x: cx, y: cy } = geometry.center;
    const radius = quarter
      ? this.visibleWheelEdgeRadius() + HD_SEASON_MARKER_EDGE_OFFSET
      : (geometry.zodiacRing.innerRadius + geometry.zodiacRing.outerRadius) / 2;
    const texts = this.seasonTexts();
    return HD_SEASON_MARKERS.map(marker => {
      const point = polarToCartesian(cx, cy, radius, longitudeToScreenAngle(marker.longitude));
      return {
        id: `mandala-season-${marker.key}`,
        key: marker.key,
        cx: point.x,
        cy: point.y,
        r: HD_SEASON_MARKER_RADIUS,
        tooltip: texts?.seasons[marker.key] ?? null,
      };
    });
  });

  protected isGateHighlighted(gate: number): boolean {
    if (!this.hoverEffectsEnabled()) {
      return false;
    }
    if (this.hoveredGate() === gate) {
      return true;
    }
    return this.hoverService?.isGateMatchingHover(gate) ?? false;
  }

  protected isAnyHoverActive(): boolean {
    if (!this.hoverEffectsEnabled()) {
      return false;
    }
    if (this.hoveredGate() != null) {
      return true;
    }
    return this.hoverService?.isActive() ?? false;
  }

  /**
   * App-setting "dimmen bij hover" staat uit. Losstaande wielen (zonder
   * geprovide hover-service, bv. cheat sheets) behouden hun gewone dim.
   */
  protected isDimDisabled(): boolean {
    return this.hoverService != null && !this.hoverService.dimOnHover();
  }

  /**
   * Of de niet-gehighlighte wieldelen moeten dimmen: de gewone hover/freeze-
   * check, tenzij "dimmen bij hover" uit staat — dan dimt er niets en tonen
   * de gehighlighte poortblokjes een blauwe rand (zie SCSS `.is-dim-disabled`).
   * Het wiel hoort bij de chart-kant: met "enkel één kant dimmen" dimt het
   * niet bij een hover vanuit de chart of het wiel zelf.
   */
  protected isDimActive(): boolean {
    return this.isAnyHoverActive() && (this.hoverService?.dimsChart() ?? true);
  }

  protected isLineHovered(id: string): boolean {
    return this.hoveredLine() === id;
  }

  protected onGateEnter(gate: number): void {
    this.emitJustNowTransitGateHover(gate);
    if (!this.hoverEffectsEnabled()) {
      this.emitMoonTransitGateHover(gate);
      return;
    }
    // De service debounced zelf; de lokale signalen debouncen we hier.
    this.hoverService?.setGateHover(gate, 'wheel-gate');
    this.scheduleHover(() => {
      this.hoveredGate.set(gate);
      // Bij een gate→gate of line→gate overgang moet een eventuele stale
      // lijn-highlight gewist worden (de gedeelde timer annuleerde anders de
      // pending line-leave-clear).
      this.hoveredLine.set(null);
    });
  }

  protected onGateLeave(): void {
    this.emitJustNowTransitGateHover(null);
    if (!this.hoverEffectsEnabled()) {
      this.emitMoonTransitGateHover(null);
      return;
    }
    this.hoverService?.setGateHover(null, 'wheel-gate');
    this.scheduleHover(() => {
      this.hoveredGate.set(null);
      this.hoveredLine.set(null);
    });
  }

  protected onLineEnter(view: MandalaLineView): void {
    this.emitJustNowTransitGateHover(view.gate);
    if (!this.hoverEffectsEnabled()) {
      this.emitMoonTransitGateHover(view.gate);
      return;
    }
    this.hoverService?.setGateHover(view.gate, 'wheel-line');
    this.scheduleHover(() => {
      this.hoveredLine.set(view.id);
      this.hoveredGate.set(view.gate);
    });
  }

  protected onLineLeave(): void {
    this.emitJustNowTransitGateHover(null);
    if (!this.hoverEffectsEnabled()) {
      this.emitMoonTransitGateHover(null);
      return;
    }
    this.hoverService?.setGateHover(null, 'wheel-line');
    this.scheduleHover(() => {
      this.hoveredLine.set(null);
      this.hoveredGate.set(null);
    });
  }

  /**
   * Plant een lokale hover-mutatie in via de gedeelde debounce-timer. Elke
   * nieuwe enter/leave annuleert de vorige pending actie (true debounce),
   * zodat snel slepen geen tussenliggende states publiceert.
   */
  private scheduleHover(action: () => void): void {
    this.cancelHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      action();
    }, HD_MANDALA_HOVER_DELAY_MS);
  }

  private cancelHoverTimer(): void {
    if (this.hoverTimer != null) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  protected onGateClick(gate: number, event: MouseEvent): void {
    const mode = this.interactionMode();

    this.gateClicked.emit(gate);

    if (mode !== 'cycle') {
      return;
    }

    if (this.primaryGateAction() === 'show-info') {
      this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
      return;
    }

    this.configurationChange.emit(cycleHdBodygraphGateInConfig(this.configuration(), gate));
  }

  protected onGateContextMenu(gate: number, event: MouseEvent): void {
    if (!this.contextMenuEnabled()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.hoverService?.suspendForContextMenu();
    this.gateContextMenuRequested.emit({ gate, position: { x: event.clientX, y: event.clientY } });
  }

  protected onMoonConnectorClick(formation: HdMoonTransitFormation, event: MouseEvent): void {
    this.moonTransitConnectorClicked.emit({ formation, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onJustNowConnectorClick(formation: HdJustNowTransitFormation, event: MouseEvent): void {
    this.justNowTransitConnectorClicked.emit({ formation, origin: { x: event.clientX, y: event.clientY } });
  }

  /**
   * Poort-hover in de maan-transit modus: enkel máánpoorten (met formaties)
   * leveren een highlight-doel op; elke andere poort werkt als clear zodat er
   * geen highlight blijft "hangen" bij het verlaten van een maanpoort.
   */
  private emitMoonTransitGateHover(gate: number | null): void {
    const transit = this.moonTransit();
    if (transit == null) {
      return;
    }
    const isMoonGate = gate != null && transit.formations.some(formation => formation.moonGate === gate);
    this.moonTransitGateHovered.emit(isMoonGate ? gate : null);
  }

  private emitJustNowTransitGateHover(gate: number | null): void {
    const transit = this.justNowTransit();
    if (transit == null) return;
    const isTransitGate = gate != null
      && transit.formations.some(formation => formation.transitGates.includes(gate));
    this.justNowTransitGateHovered.emit(isTransitGate ? gate : null);
  }
}

/**
 * Licht naar binnen gebogen verbindingslijn (kwadratische Bézier): het
 * controlepunt is het koorde-midden, richting het wielmiddelpunt getrokken.
 * De pull groeit mee met de hoekafstand — net genoeg buiging om de
 * poortnummers/spaken te ontwijken zonder onnatuurlijk diep te duiken (§6.2).
 */
function describeInwardCurve(
  from: { x: number; y: number },
  to: { x: number; y: number },
  cx: number,
  cy: number,
  fromAngle: number,
  toAngle: number,
): string {
  const chordMidX = (from.x + to.x) / 2;
  const chordMidY = (from.y + to.y) / 2;
  const span = angularSpan(fromAngle, toAngle);
  const pull = Math.min(0.35, 0.08 + (span / 180) * 0.3);
  const controlX = cx + (chordMidX - cx) * (1 - pull);
  const controlY = cy + (chordMidY - cy) * (1 - pull);
  return `M ${from.x} ${from.y} Q ${controlX} ${controlY} ${to.x} ${to.y}`;
}

/**
 * Boogstraal van de lock↔key-verbinding als fractie van de koorde tussen de
 * twee wigpunten. Net boven 0,5 (een halve cirkel) geeft een diepe, zachte
 * cirkelboog; 0,51 → 0,54 in bijstuurronde 3 (een tikje vlakker).
 */
const HD_MANDALA_LOCKS_KEYS_CONNECTOR_ARC_RADIUS_FACTOR = 0.54;

/**
 * Cirkelboog tussen twee wigpunten die richting het wielcentrum doorbuigt.
 * De sweep-kant wordt uit de ligging van het centrum t.o.v. de koorde
 * afgeleid, zodat de boog aan beide kanten van het wiel altijd naar binnen
 * bolt.
 */
function describeLocksKeysConnector(
  from: { x: number; y: number },
  to: { x: number; y: number },
  cx: number,
  cy: number,
): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const radius = Math.hypot(dx, dy) * HD_MANDALA_LOCKS_KEYS_CONNECTOR_ARC_RADIUS_FACTOR;
  // Kruisproduct: ligt het wielcentrum links van de richting from→to, dan
  // bolt sweep=1 (de linker cirkelboog) naar het centrum toe.
  const centerCross = dx * (cy - from.y) - dy * (cx - from.x);
  const sweep = centerCross < 0 ? 1 : 0;
  return `M ${from.x} ${from.y} A ${radius} ${radius} 0 0 ${sweep} ${to.x} ${to.y}`;
}

/** Gesloten polygon-pad door de gegeven punten (de ster-vierkanten). */
function describeClosedPolygon(points: readonly { x: number; y: number }[]): string {
  if (points.length === 0) {
    return '';
  }
  return `${points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ')} Z`;
}

/**
 * Naar binnen wijzende punt aan een lock-venster: een gevulde driehoek met de
 * basis op de binnenrand van het poortsegment en de tip richting het
 * wielcentrum — de "spie-wig" uit het Wheel of 2027-referentiebeeld. Kruist
 * bewust de eventuele binnenringen (dierenriem/godheads), net als de
 * today-naald dat buitenwaarts doet.
 */
function describeInwardPointer(
  cx: number,
  cy: number,
  segment: MandalaGateSegment,
  length: number,
): string {
  const baseRadius = segment.innerRadius;
  const tip = polarToCartesian(cx, cy, baseRadius - length, segment.midAngle);
  const baseA = polarToCartesian(
    cx, cy, baseRadius, segment.midAngle - HD_MANDALA_LOCKS_KEYS_POINTER_HALF_ANGLE,
  );
  const baseB = polarToCartesian(
    cx, cy, baseRadius, segment.midAngle + HD_MANDALA_LOCKS_KEYS_POINTER_HALF_ANGLE,
  );
  return `M ${baseA.x} ${baseA.y} L ${tip.x} ${tip.y} L ${baseB.x} ${baseB.y} Z`;
}

/** Kleinste hoekafstand tussen twee schermhoeken, in graden [0, 180]. */
function angularSpan(angleA: number, angleB: number): number {
  const diff = Math.abs(normalizeAngle(angleA) - normalizeAngle(angleB)) % 360;
  return diff > 180 ? 360 - diff : diff;
}

/**
 * Ligt een schermhoek in het onderste kwadrant-venster (rond 6 uur)? Bepaalt
 * welk kwartaal de omgekeerde tekstboog krijgt: tekst op een gewone boog zou
 * daar ondersteboven staan. Bewust smal (135–225): de linker- en rechter-
 * kwartaalteksten lezen per spec verticaal mee met de cirkel.
 */
function isBottomHalfAngle(angleDeg: number): boolean {
  const angle = normalizeAngle(angleDeg);
  return angle > 135 && angle < 225;
}

/**
 * Onderste halve cirkel (90–270): het omkeer-venster voor fijnmazige ringen
 * zoals de 16 godhead-segmenten. Segmenten nét onder de horizontale as
 * (bv. Janus op ±236°, Thoth op ±124°) zouden met het smalle kwartaal-venster
 * voorbij verticaal kantelen en ondersteboven lezen; de klassieke
 * halve-cirkel-regel houdt elke naam binnen een kwartslag van rechtop.
 */
function isLowerSemicircleAngle(angleDeg: number): boolean {
  const angle = normalizeAngle(angleDeg);
  return angle > 90 && angle < 270;
}
